import { useState } from "react";
import { toast } from "sonner";
import { startCheckout } from "@/lib/api";
import { Loader2, X } from "lucide-react";

/**
 * Minimal checkout modal. Collects customer email/name then redirects to Stripe.
 * Styled to work on top of either brand (dark base).
 */
export default function BuyModal({ open, onClose, item, brand = "framework" }) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);

    if (!open || !item) return null;
    const accent = brand === "framework" ? "#a6ce39" : "#e8e6df";
    const textAccent = brand === "framework" ? "text-[#a6ce39]" : "text-[#f4f2ec]";

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return toast.error("Email required for order updates.");
        setBusy(true);
        try {
            await startCheckout({
                item_type: item.type,
                item_id: item.id,
                quantity: item.quantity || 1,
                brand,
                customer_email: email,
                customer_name: name,
            });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not start checkout");
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-6" onClick={onClose} data-testid="buy-modal">
            <div className="w-full max-w-md bg-[#0f0f10] border border-white/15 p-8 relative" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white" data-testid="buy-modal-close">
                    <X size={18} />
                </button>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Checkout</div>
                <h3 className={`text-2xl font-semibold ${textAccent}`}>{item.name}</h3>
                <p className="text-white/60 text-sm mt-2">{item.description}</p>
                <div className="mt-4 flex items-baseline gap-2">
                    <span className="text-3xl font-semibold" style={{ color: accent }}>${item.price?.toFixed(2)}</span>
                    <span className="text-xs uppercase tracking-[0.2em] text-white/40">USD</span>
                </div>

                <form onSubmit={submit} className="mt-6 space-y-3">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.25em] text-white/50">Your name</span>
                        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="buy-modal-name"
                            className="mt-1 w-full bg-transparent border border-white/20 px-3 py-2 text-white outline-none focus:border-[#a6ce39]" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.25em] text-white/50">Email *</span>
                        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="buy-modal-email"
                            className="mt-1 w-full bg-transparent border border-white/20 px-3 py-2 text-white outline-none focus:border-[#a6ce39]" />
                    </label>
                    <button type="submit" disabled={busy} data-testid="buy-modal-submit"
                        className="w-full mt-4 py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                        style={{ background: accent, color: "#000" }}>
                        {busy ? <><Loader2 className="animate-spin" size={16} /> Redirecting…</> : `Pay $${item.price?.toFixed(2)} securely`}
                    </button>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 text-center mt-2">
                        Powered by Stripe · Test mode
                    </p>
                </form>
            </div>
        </div>
    );
}
